// pages/ContributorFeedPage.jsx — Anamoria SPA
// v2.9 — NEW-163 Increment B (2026-10-30): the member can respond to the leader's note. GroupNoteBanner v1.2
//   gets canReply (this page is the contributor rail, never the leader), spaceId and videoEnabled so its
//   Respond row can open the capture pages with ?note=<id>. ContributorMemoryFeed v1.10 gets spaceIsGroup +
//   groupNotes so a tagged memory (groupNoteId) renders as a response card and resolves its pill. SPA build
//   spec v0.2 §3. No change to the space fetch.
// v2.8 — NEW-162 Increment A (2026-10-29): GroupNoteBanner (read-only) under the contribution banner when the
//   space is a group space and group_spaces_enabled — data = space.groupNotes from GET /me/contributions/{spaceId}
//   (anamoria-contributors v1.38). Renders nothing until the leader has shared a note. One-way (no Reply yet).
// E3a (Consent PR E, Oct 4, 2026): + KeepCopySwitch under the contribution banner (dark unless the server flag is on).
// v2.7 — NEW-122 (2026-09-27 fix): corrected the entry-point button's POSITION only.
//   Copy ("Start your own space") was already correct per the settled decision in
//   Contributor-and-Consent/Anamoria_Contributor_Conversion_Copy_v0.1.md (2026-09-27) —
//   an earlier fix pass in this same incident briefly, wrongly, changed it to "Create
//   your own space" (the plan's stale placeholder text) and that was reverted.
//   The real bug: this button rendered BEFORE avatar/spaceInfo, so its
//   margin-left:auto pulled the whole trailing group left-ward with it instead of
//   sitting alone at the header's right edge (approved top-right placement) — now
//   rendered after avatar/spaceInfo instead. No prop/behavior change.
// v2.6 — NEW-122: "Start your own space" entry point for a pure contributor
//   (!mySpaceId). Mirrors the existing mySpaceId backLink branch, opposite side.
//   New props: onCreateOwnSpace (click handler, owned by App.jsx) and
//   createSpaceError (a resolved, ready-to-render message or null — this
//   component does not know about /pilot/join error codes, App.jsx resolves
//   them). Renders as a small notice below the header, not a page takeover.
// v2.5 — NEW-65 (PR-7b): tap a SHARED memory (not your own) to open the read-only
//   detail view. handleViewMemory navigates to /contributing/{spaceId}/memories/{id}
//   with the memory in router state; passes onViewMemory to the feed. Own cards keep
//   the pencil (edit) only — no body tap.
// v2.4 — NEW-19/copy fix: extract ownerName from the handleContributorSpace response and pass it
//   as a prop to ContributorMemoryFeed so the share-request consent prompt renders the correct
//   thanatologist-approved copy. Requires anamoria-contributors v1.22.
// v2.3 — NEW-16: contributor home/back-nav. The header back control is now
//   authorization-aware via the mySpaceId prop (from App.jsx appState):
//   - contributor who ALSO owns a space (mySpaceId set) → explicit "Go to my
//     space" link to /spaces/{mySpaceId} (replaces the implicit back-arrow that
//     used to navigate('/') and rely on the root redirect).
//   - pure contributor (mySpaceId null, owns no space) → NO back control (the
//     feed IS their home; the old navigate('/') dead-ended on /spaces/new, 403).
//   Frontend-only; no backend lookup (owned spaces already known to bootstrap).
// v2.2 — Account-link access mode (May 31, 2026)
//
// Changes from v2.1:
//   - Added `mode` prop ('session' | 'linked', default 'session'). Default
//     preserves existing public-route behaviour byte-for-byte (no regression).
//   - Data layer now sourced from getContributorAccess(mode, getAccessTokenSilently)
//     instead of hardcoding createContributorApiClient() + '/contribute'.
//     Linked mode → owner JWT client + '/me/contributions'; session mode →
//     contributor session client + '/contribute'.
//   - Navigation (back, edit) is prefix-aware: '/contributing' in linked mode,
//     '/contribute' in session mode.
//   - Mode-aware expiry copy: linked → "please sign in again"; session →
//     existing "use your invite link again".
//   - No sessionStorage token used in linked mode (R-NOSTORE).
//
// v2.1 — Contributor edit navigation (May 25, 2026)
//
// Changes from v2.0:
//   - Added handleEditMemory callback — navigates to the correct edit page
//     based on memory.category, passing editMemory via location.state.
//   - Passes onEditMemory to ContributorMemoryFeed.
//
// v2.0 — Session 3 (April 19, 2026)
//
// Contributor feed screen. Reached via "View shared memories →" link from
// ContributorHomePage (Screen 1). Back chevron returns to ContributorHomePage.
//
// Route: /contribute/:spaceId/memories
//
// Ported from LWC axr_MemoryVaultV2.html contributor-feed-screen (v2.9) +
// axr_MemoryVaultV2.css contributor-space-header, contributor-banner,
// contributor-feed-screen blocks.
//
// Changes from v1.0 (current deployed):
//   - Route changed from /contribute/:spaceId to /contribute/:spaceId/memories
//     (root /contribute/:spaceId is now ContributorHomePage).
//   - Removed inline sessionFetch helper — uses createContributorApiClient() from
//     api/contributorApi.js instead.
//   - Removed custom inline card rendering — delegates to ContributorMemoryFeed
//     component (Step 4 deliverable, uses MemoryFeed.module.css via B2 reuse).
//   - Removed inline CTA buttons — capture actions now in ContributorBottomNav
//     (Step 3 deliverable).
//   - New header structure: back chevron + avatar + space name + "Shared Memories"
//     subtitle (replaces old "ANAMORIA" logo + space name + "Welcome, {name}").
//   - New contribution banner below header: "You're contributing to {spaceName}'s
//     memory space" (sage gradient, full-width).
//   - Uses camelCase response fields from Lambda v1.2 (spaceName, contributorName,
//     photoUrl) per B1 response shape changes.
//
// Auth: Auth0 JWT (linked account) via getContributorAccess('linked'); the route
// is behind ProtectedRoute. Track B (B-2) — the session-token path is retired.
//
// Layout:
//   [screen]
//     [contributor-space-header: ←  [avatar] Space name / "Shared Memories"]
//     [contribution-banner: 👥 "You're contributing to X's memory space"]
//     [ContributorMemoryFeed — masonry, handles empty state]
//   [ContributorBottomNav — Record / Write / Photo]

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getContributorAccess } from '../api/contributorAccess';
import ContributorMemoryFeed from '../components/ContributorMemoryFeed';
import GroupNoteBanner from '../components/GroupNoteBanner';   // v2.8: NEW-162 leader note (read-only)
import ContributorBottomNav from '../components/ContributorBottomNav';
import styles from './ContributorFeedPage.module.css';
import KeepCopySwitch from '../components/KeepCopySwitch'; // E3a

/* ─── Inline SVG icons ─── */

function BackArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ContributorsIcon() {
  // People icon matching the LWC banner decoration — two overlapping user shapes.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

// v2.8 — NEW-127: contributor sign-out. Icon-only (SpacePage's existing
// sign-out glyph, reused as-is for cross-role consistency), hover-reveal
// label on desktop only (::after in CSS), aria-label carries the accessible
// name on touch/screen readers regardless of hover support.
function SignOutIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/* ═══════════════════════════════════════
   CONTRIBUTOR FEED PAGE
   ═══════════════════════════════════════ */

export default function ContributorFeedPage({
  mode = 'linked',
  mySpaceId = null,
  onCreateOwnSpace,
  createSpaceError = null,
}) {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, logout } = useAuth0();

  const navBase = '/contributing';
  const expiredMsg = 'Your session has expired. Please sign in again.';

  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Single source of truth for client + prefix, bound to the access mode
  // (R-ADAPTER). Computed once per [mode, getAccessTokenSilently].
  const access = useMemo(
    () => getContributorAccess(mode, getAccessTokenSilently),
    [mode, getAccessTokenSilently]
  );

  // Stable getApi for ContributorMemoryFeed (mirrors owner SpacePage factory pattern).
  const getApi = useCallback(() => access.api, [access]);

  /* ─── Validate access + fetch space metadata ─── */
  useEffect(() => {
    // Track B (B-2): linked-only. Auth is the Auth0 JWT (route is behind
    // ProtectedRoute); no session-token guard.
    let cancelled = false;
    async function load() {
      try {
        const data = await access.api.get(`${access.prefix}/${spaceId}`);
        if (cancelled) return;
        setSpace(data);
      } catch (err) {
        if (cancelled) return;
        if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
          setError(expiredMsg);
        } else {
          setError('Could not load this space.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, access, expiredMsg]);

  /* ─── NEW-16: back → the contributor's OWN space (only when they own one).
     A pure contributor owns no space (mySpaceId null) and the back control is
     not rendered — the feed is their home. The old navigate('/') dead-ended on
     /spaces/new (owner-create, 403) for a pure contributor. ─── */
  const handleBack = useCallback(() => {
    if (mySpaceId) navigate(`/spaces/${mySpaceId}`);
  }, [navigate, mySpaceId]);

  /* ─── NEW-127: sign out. Same pattern as SettingsPage.jsx's handleSignOut
     (owner/member) — clear ana_* sessionStorage keys before the Auth0
     redirect, so a shared/public device doesn't retain invite-flow state. ─── */
  const handleSignOut = useCallback(() => {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('ana_')) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  /* ─── v2.1: Edit memory — navigate to correct edit page (prefix-aware) ─── */
  const handleEditMemory = useCallback((memory) => {
    const category = (memory.category || '').toLowerCase();
    if (category === 'voice') {
      navigate(`${navBase}/${spaceId}/record`, { state: { editMemory: memory } });
    } else if (category === 'photo') {
      navigate(`${navBase}/${spaceId}/photo`, { state: { editMemory: memory } });
    } else if (category === 'video') {
      // NEW-128: previously fell through to /write (the text editor) with a
      // video memory object -- routes to the video page now, matching the
      // voice/photo pattern above.
      navigate(`${navBase}/${spaceId}/video`, { state: { editMemory: memory } });
    } else {
      navigate(`${navBase}/${spaceId}/write`, { state: { editMemory: memory } });
    }
  }, [navigate, navBase, spaceId]);

  /* ─── v2.5 NEW-65: View a shared memory the contributor did NOT author →
     read-only detail. Memory passed via router state (Option A). ─── */
  const handleViewMemory = useCallback((memory) => {
    navigate(`${navBase}/${spaceId}/memories/${memory.id}`, { state: { viewMemory: memory } });
  }, [navigate, navBase, spaceId]);

  /* ─── Render states ─── */

  if (loading) {
    return (
      <div className={styles.loadingScreen}>
        <div className={styles.loadingDot} />
        <span>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorScreen}>
        <p className={styles.errorText}>{error}</p>
      </div>
    );
  }

  const spaceName = space?.spaceName || 'this space';
  const ownerName = space?.ownerName || 'The space owner';
  const initial = (spaceName || '?').charAt(0).toUpperCase();

  return (
    <div className={styles.screen}>

      {/* ─── Space header: [go to my space] + avatar + name + "Shared Memories" ─── */}
      {/* v2.8 — NEW-127: restructured into three explicit zones (start/center/end)
          via styles.header's justify-content:space-between, replacing the old
          single-flex-row + margin-left:auto approach that caused the NEW-122
          position bug (whichever element was last in DOM order got pushed,
          regardless of intent). Conditional elements (backLink, createSpaceBtn)
          and the new persistent sign-out control each sit in their own zone now,
          so layout no longer depends on which conditional ones render. */}
      <header className={styles.header}>
        <div className={styles.headerStart}>
          {/* NEW-16: only a contributor who ALSO owns a space gets a back control,
              as an explicit "Go to my space" link. A pure contributor sees none —
              this shared feed is their home. */}
          {mySpaceId && (
            <button
              type="button"
              className={styles.backLink}
              onClick={handleBack}
              aria-label="Go to my space"
            >
              <BackArrowIcon />
              <span className={styles.backLinkLabel}>Go to my space</span>
            </button>
          )}
        </div>

        <div className={styles.headerCenter}>
          <div className={styles.avatar}>
            {space?.photoUrl ? (
              <img
                src={space.photoUrl}
                alt={spaceName}
                className={styles.avatarImg}
              />
            ) : (
              <span className={styles.avatarPlaceholder}>{initial}</span>
            )}
          </div>

          <div className={styles.spaceInfo}>
            <h1 className={styles.spaceName}>{spaceName}</h1>
            <p className={styles.spaceSubtitle}>Shared Memories</p>
          </div>
        </div>

        <div className={styles.headerEnd}>
          {/* NEW-122 (fixed 2026-09-27): mirror-image of the old backLink slot — a
              pure contributor (owns no space) gets a self-serve entry point
              instead. Mutually exclusive with backLink (mySpaceId is either set
              or it isn't), and additionally gated on the server-resolved flag so
              it stays hidden while contributor_conversion_enabled is off. Copy is
              unchanged and was already correct: "Start your own space", settled
              2026-09-27 in
              Contributor-and-Consent/Anamoria_Contributor_Conversion_Copy_v0.1.md
              (register + accuracy + grief lens), superseding the plan's own stale
              "Create your own space" placeholder. */}
          {!mySpaceId && space?.contributorConversionEnabled && (
            <button
              type="button"
              className={styles.createSpaceBtn}
              onClick={onCreateOwnSpace}
              aria-label="Start your own space"
            >
              <span className={styles.createSpaceBtnLabel}>Start your own space</span>
            </button>
          )}

          {/* NEW-127: sign out. Icon-only, hover-reveal label on desktop
              (styles.signOutBtn::after), aria-label carries the accessible name
              on touch/screen readers. Always rendered — every contributor here
              is Auth0-authenticated (linked mode only; see access.mode). */}
          <button
            type="button"
            className={styles.signOutBtn}
            onClick={handleSignOut}
            aria-label="Sign out"
            data-tip="Sign out"
          >
            <SignOutIcon />
          </button>
        </div>
      </header>

      {/* NEW-122: a resolved, ready-to-render message from a failed create-own-space
          attempt (or null). App.jsx owns the /pilot/join error-code -> message
          mapping; this component just renders whatever it's handed, as a small
          notice — not a full-page takeover (that pattern is reserved for a
          bootstrap-time failure, not a mid-session recoverable one). */}
      {createSpaceError && (
        <div className={styles.createSpaceNotice} role="status">
          {createSpaceError}
        </div>
      )}

      {/* ─── Contribution banner ─── */}
      <div className={styles.banner}>
        <span className={styles.bannerIcon}><ContributorsIcon /></span>
        <span className={styles.bannerText}>
          You're contributing to {spaceName}'s memory space
        </span>
      </div>

      {/* E3a (Consent PR E): the contributor's "keep a copy" switch — renders nothing unless the
          server says keepCopyCaptureEnabled (dark) AND, when off, the notice text exists. */}
      <KeepCopySwitch
        space={space}
        api={access.api}
        prefix={access.prefix}
        spaceId={spaceId}
        onChange={(allowed) => setSpace((prev) => (prev ? { ...prev, keepCopyAllowed: allowed } : prev))}
      />

      {/* ─── Masonry feed (empty state handled inside component) ─── */}
      <main className={styles.main}>
        {/* v2.8 (NEW-162): the leader's note to the group — read-only for a member, first in the feed
            (mockup v0.9 placement C); nothing until the leader has shared one. */}
        {space?.isGroupSpace === true && space?.groupSpacesEnabled === true && (
          <GroupNoteBanner
            groupNotes={space.groupNotes}
            leaderName={space.ownerName}
            canShare={false}
            canReply   /* v2.9 (NEW-163): members only — this page is never the leader */
            spaceId={spaceId}
            videoEnabled={space?.contributorVideoEnabled === true}
          />
        )}
        <ContributorMemoryFeed
          spaceId={spaceId}
          getApi={getApi}
          prefix="/me/contributions"
          onEditMemory={handleEditMemory}
          onViewMemory={handleViewMemory}
          ownerName={ownerName}
          spaceIsGroup={space?.isGroupSpace === true && space?.groupSpacesEnabled === true}   /* v2.9 (NEW-163) */
          groupNotes={space?.groupNotes}
        />
      </main>

      {/* ─── Bottom nav: Record / Write / Photo ─── */}
      <ContributorBottomNav spaceId={spaceId} mode={mode} videoEnabled={space?.contributorVideoEnabled} />

    </div>
  );
}
